import React, { useEffect, useRef, useState } from 'react';
import { API } from '../../utils/api';
import WhiskeyLogo from './WhiskeyLogo';
import LucideIcon from '../LucideIcon';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

const panel: React.CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14,
};

/**
 * The floating Ask Whiskey launcher + chat panel, present on every
 * authenticated screen (mounted once by AppShell). Replaces the earlier
 * permanently-disabled "coming soon" FAB -- see the 2026-08-28 UX audit --
 * with a real, working companion backed by POST/GET /whiskey-chat.
 */
const WhiskeyChat: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!open || historyLoaded || historyLoading) return;
    setHistoryLoading(true);
    API.get('/whiskey-chat')
      .then((res: any) => setMessages(Array.isArray(res?.messages) ? res.messages : []))
      .catch(() => {
        /* start with an empty conversation rather than blocking the panel on a failed load */
      })
      .finally(() => {
        setHistoryLoaded(true);
        setHistoryLoading(false);
      });
  }, [open, historyLoaded, historyLoading]);

  useEffect(() => {
    if (!open) return;
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending, open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setError('');
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setSending(true);
    try {
      const res: any = await API.post('/whiskey-chat', { message: text });
      setMessages((prev) => [...prev, { role: 'assistant', content: res.reply }]);
    } catch (e: any) {
      setError(e?.message || "Couldn't reach Whiskey. Try again.");
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {open && (
        <div
          className="ws-rise ws-whiskey-panel"
          style={{
            ...panel,
            position: 'fixed', zIndex: 20, display: 'flex', flexDirection: 'column',
            right: 20, bottom: 92, width: 360, maxWidth: 'calc(100vw - 40px)', height: 480, maxHeight: 'calc(100vh - 140px)',
            boxShadow: '0 12px 32px oklch(0.2 0.02 260 / 0.2)', overflow: 'hidden',
          }}
          role="dialog"
          aria-label="Chat with Whiskey"
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: '1px solid var(--border)', flex: '0 0 auto' }}>
            <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--surface-sunken)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
              <WhiskeyLogo size={20} />
            </div>
            <div style={{ minWidth: 0 }}>
              <p className="ws-display" style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Whiskey</p>
              <p style={{ margin: 0, fontSize: 11, color: 'var(--text-tertiary)' }}>Ask about prompts, techniques, or the app</p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close chat"
              style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', display: 'flex', padding: 4 }}
            >
              <LucideIcon name="X" size={17} />
            </button>
          </div>

          <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {historyLoading && messages.length === 0 && (
              <p className="ws-mono" style={{ margin: 'auto', fontSize: 12, color: 'var(--text-tertiary)' }}>Loading&hellip;</p>
            )}
            {!historyLoading && messages.length === 0 && (
              <div style={{ margin: 'auto', textAlign: 'center', padding: '0 12px' }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}><WhiskeyLogo size={28} /></div>
                <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  Ruff! Ask me about a prompting technique, get feedback on something you're writing, or ask what to try next.
                </p>
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                style={{
                  alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '85%',
                  background: m.role === 'user' ? 'var(--ember)' : 'var(--surface-sunken)',
                  color: m.role === 'user' ? 'white' : 'var(--text-primary)',
                  border: m.role === 'user' ? 'none' : '1px solid var(--border)',
                  borderRadius: 12,
                  padding: '9px 12px',
                  fontSize: 13,
                  lineHeight: 1.55,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {m.content}
              </div>
            ))}
            {sending && (
              <div style={{ alignSelf: 'flex-start', background: 'var(--surface-sunken)', border: '1px solid var(--border)', borderRadius: 12, padding: '9px 12px' }}>
                <span className="ws-mono" style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Whiskey is thinking&hellip;</span>
              </div>
            )}
          </div>

          {error && (
            <p role="alert" style={{ margin: '0 16px 8px', fontSize: 12, color: 'var(--danger, #b42318)' }}>{error}</p>
          )}

          <div style={{ display: 'flex', gap: 8, padding: 12, borderTop: '1px solid var(--border)', flex: '0 0 auto' }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask Whiskey anything about prompting…"
              rows={1}
              disabled={sending}
              style={{
                flex: 1, resize: 'none', fontSize: 13, lineHeight: 1.4, padding: '9px 12px', maxHeight: 80,
                color: 'var(--text-primary)', background: 'var(--surface-sunken)', border: '1px solid var(--border)',
                borderRadius: 10, outline: 'none', fontFamily: 'inherit',
              }}
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={!input.trim() || sending}
              aria-label="Send message"
              className="btn-live"
              style={{
                flex: '0 0 auto', width: 38, height: 38, borderRadius: 10, border: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: !input.trim() || sending ? 'not-allowed' : 'pointer',
                background: !input.trim() || sending ? 'var(--surface-sunken)' : 'var(--ember)',
                color: !input.trim() || sending ? 'var(--text-tertiary)' : 'white',
              }}
            >
              <LucideIcon name="Send" size={16} />
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="ws-shell-fab-desktop btn-live"
        aria-label={open ? 'Close chat with Whiskey' : 'Ask Whiskey'}
        style={{
          position: 'fixed', right: 28, bottom: 28, zIndex: 20, display: 'flex', alignItems: 'center', gap: 10,
          background: 'var(--surface)', border: '1.5px solid var(--border-strong)', borderRadius: 999,
          padding: '8px 16px 8px 8px', boxShadow: '0 4px 16px oklch(0.2 0.02 260 / 0.14)', cursor: 'pointer',
        }}
      >
        <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--surface-sunken)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <WhiskeyLogo size={22} />
        </div>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-secondary)' }}>{open ? 'Close' : 'Ask Whiskey'}</span>
      </button>

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="ws-shell-fab-mobile btn-live"
        aria-label={open ? 'Close chat with Whiskey' : 'Ask Whiskey'}
        style={{
          position: 'fixed', right: 16, bottom: 76, zIndex: 20, alignItems: 'center', gap: 8,
          background: 'var(--surface)', border: '1.5px solid var(--border-strong)', borderRadius: 999,
          padding: '7px 14px 7px 7px', boxShadow: '0 4px 16px oklch(0.2 0.02 260 / 0.14)', cursor: 'pointer',
        }}
      >
        <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--surface-sunken)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <WhiskeyLogo size={18} />
        </div>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)' }}>{open ? 'Close' : 'Ask Whiskey'}</span>
      </button>
    </>
  );
};

export default WhiskeyChat;
